import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Root-Layout mit Auth-Status Anzeige und globalem Dark-Theme.
 * Wartet explizit auf Clerk-Initialisierung um den weißen Bildschirm
 * beim ersten Laden zu verhindern.
 */
function RootLayout() {
  const { userId, isLoaded } = useAuth();

  // Solange Clerk noch lädt, einen Splash-Screen zeigen statt leerer Seite.
  // Das verhindert den weißen Bildschirm beim ersten Öffnen der PWA.
  if (!isLoaded) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: '#0f0f23',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '3px solid #333',
            borderTop: '3px solid #4ade80',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Wird geladen...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0f0f23',
        color: '#ffffff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Auth status bar — nur wenn Clerk geladen und Nutzer nicht eingeloggt */}
      {!userId && (
        <div
          style={{
            backgroundColor: '#2d1515',
            borderBottom: '1px solid #991b1b',
            padding: '8px 16px',
            fontSize: '13px',
            color: '#f87171',
            textAlign: 'center',
          }}
        >
          Nicht angemeldet — bitte einloggen
        </div>
      )}

      <Outlet />
    </div>
  );
}

/**
 * Zeigt einen Banner wenn keine Netzwerkverbindung besteht.
 * Reagiert dynamisch auf Online/Offline-Ereignisse des Browsers.
 */
function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof window !== 'undefined' ? window.navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      style={{
        backgroundColor: '#2b1f0d',
        borderBottom: '1px solid #92400e',
        padding: '8px 16px',
        fontSize: '13px',
        color: '#fbbf24',
        textAlign: 'center',
      }}
    >
      Kein Internet — App läuft offline
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});
